import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
} from "@asm/ui";
import { AlertCircle, ArrowLeft, KeyRound, Lock, Mail, Shield, Smartphone, Copy, Check, CheckCircle2 } from "lucide-react";
import { useAuthStore } from "@/lib/auth";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [totpSetupCode, setTotpSetupCode] = useState("");
  const [secretCopied, setSecretCopied] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");

  const {
    login,
    confirmTotp,
    confirmNewPassword,
    setupTotp,
    forgotPassword,
    confirmPasswordReset,
    resetAuthStep,
    isLoading,
    error,
    clearError,
    isAuthenticated,
    authStep,
    totpSetupSecret,
    resetSuccess,
    pendingEmail,
  } = useAuthStore();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await login(email, password);
    } catch {
      // Error is handled by the store
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await confirmNewPassword(newPassword);
    } catch {
      // Error is handled by the store
    }
  };

  const handleTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await confirmTotp(totpCode);
    } catch {
      // Error handled by store
    }
  };

  const handleTotpSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await setupTotp(totpSetupCode);
    } catch {
      // Error handled by store
    }
  };

  const handleCopySecret = async () => {
    if (totpSetupSecret) {
      await navigator.clipboard.writeText(totpSetupSecret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await forgotPassword(resetEmail);
    } catch {
      // Error handled by store
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await confirmPasswordReset(resetCode, resetNewPassword);
      setResetCode("");
      setResetNewPassword("");
      setResetEmail("");
    } catch {
      // Error handled by store
    }
  };

  const handleBack = () => {
    resetAuthStep();
    setTotpCode("");
    setTotpSetupCode("");
    setNewPassword("");
    setResetCode("");
    setResetNewPassword("");
    setResetEmail("");
  };

  const handleStartForgotPassword = () => {
    clearError();
    setResetEmail(email);
    useAuthStore.setState({ authStep: "forgot_password", resetSuccess: false });
  };

  // TOTP setup form
  if (authStep === "totp_setup") {
    return (
      <Card className="border-border bg-card/80 backdrop-blur animate-fade-in-up">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <Smartphone className="h-6 w-6 text-red-400" />
            </div>
          </div>
          <CardTitle className="text-2xl">Set Up Authenticator</CardTitle>
          <CardDescription>
            Add this account to your authenticator app (Google Authenticator, Authy, etc.)
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleTotpSetup}>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            {totpSetupSecret && (
              <div className="space-y-2">
                <Label>Secret Key</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-sm break-all select-all">
                    {totpSetupSecret}
                  </code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="flex-shrink-0"
                    onClick={handleCopySecret}
                  >
                    {secretCopied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Enter this key manually in your authenticator app
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="totpSetupCode">Verification Code</Label>
              <Input
                id="totpSetupCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={totpSetupCode}
                onChange={(e) => setTotpSetupCode(e.target.value.replace(/\D/g, ""))}
                required
                autoComplete="one-time-code"
                autoFocus
                className="bg-muted/50 border-border focus:border-red-500/50 text-center font-mono text-2xl tracking-[0.5em]"
              />
              <p className="text-xs text-muted-foreground text-center">
                Enter the 6-digit code shown in your authenticator app
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            <Button
              type="submit"
              className="w-full bg-red-500 hover:bg-red-400 text-white font-semibold"
              disabled={isLoading || totpSetupCode.length !== 6}
            >
              {isLoading ? "Verifying..." : "Verify & Complete"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground"
              onClick={handleBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to login
            </Button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  if (authStep === "new_password") {
    return (
      <Card className="border-border bg-card/80 backdrop-blur animate-fade-in-up">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <KeyRound className="h-6 w-6 text-red-400" />
            </div>
          </div>
          <CardTitle className="text-2xl">Set New Password</CardTitle>
          <CardDescription>
            Please set a new password to continue
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleNewPassword}>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  autoFocus
                  className="bg-muted/50 border-border focus:border-red-500/50 pl-10"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                At least 8 characters with uppercase, lowercase, and number
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            <Button
              type="submit"
              className="w-full bg-red-500 hover:bg-red-400 text-white font-semibold"
              disabled={isLoading}
            >
              {isLoading ? "Setting password..." : "Set Password"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground"
              onClick={handleBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to login
            </Button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  if (authStep === "totp") {
    return (
      <Card className="border-border bg-card/80 backdrop-blur animate-fade-in-up">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <Shield className="h-6 w-6 text-red-400" />
            </div>
          </div>
          <CardTitle className="text-2xl">Two-Factor Authentication</CardTitle>
          <CardDescription>
            Enter the code from your authenticator app
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleTotp}>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="totpCode">Authentication Code</Label>
              <Input
                id="totpCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                required
                autoComplete="one-time-code"
                autoFocus
                className="bg-muted/50 border-border focus:border-red-500/50 text-center font-mono text-2xl tracking-[0.5em]"
              />
              <p className="text-xs text-muted-foreground text-center">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            <Button
              type="submit"
              className="w-full bg-red-500 hover:bg-red-400 text-white font-semibold"
              disabled={isLoading || totpCode.length !== 6}
            >
              {isLoading ? "Verifying..." : "Verify"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground"
              onClick={handleBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to login
            </Button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  // Forgot password — enter email to receive reset code
  if (authStep === "forgot_password") {
    return (
      <Card className="border-border bg-card/80 backdrop-blur animate-fade-in-up">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <KeyRound className="h-6 w-6 text-red-400" />
            </div>
          </div>
          <CardTitle className="text-2xl">Reset Password</CardTitle>
          <CardDescription>
            Enter your email address and we'll send you a verification code
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleForgotPassword}>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="resetEmail">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="resetEmail"
                  type="email"
                  placeholder="admin@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                  className="bg-muted/50 border-border focus:border-red-500/50 pl-10"
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            <Button
              type="submit"
              className="w-full bg-red-500 hover:bg-red-400 text-white font-semibold"
              disabled={isLoading || !resetEmail}
            >
              {isLoading ? "Sending code..." : "Send Reset Code"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground"
              onClick={handleBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to login
            </Button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  // Reset password — enter code and new password
  if (authStep === "reset_password") {
    return (
      <Card className="border-border bg-card/80 backdrop-blur animate-fade-in-up">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <Lock className="h-6 w-6 text-red-400" />
            </div>
          </div>
          <CardTitle className="text-2xl">Set New Password</CardTitle>
          <CardDescription>
            We sent a verification code to {pendingEmail}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleConfirmReset}>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="resetCode">Verification Code</Label>
              <Input
                id="resetCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ""))}
                required
                autoComplete="one-time-code"
                autoFocus
                className="bg-muted/50 border-border focus:border-red-500/50 text-center font-mono text-2xl tracking-[0.5em]"
              />
              <p className="text-xs text-muted-foreground text-center">
                Enter the 6-digit code sent to your email
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="resetNewPassword">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="resetNewPassword"
                  type="password"
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="bg-muted/50 border-border focus:border-red-500/50 pl-10"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                At least 8 characters with uppercase, lowercase, and number
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            <Button
              type="submit"
              className="w-full bg-red-500 hover:bg-red-400 text-white font-semibold"
              disabled={isLoading || resetCode.length !== 6 || !resetNewPassword}
            >
              {isLoading ? "Resetting password..." : "Reset Password"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground"
              onClick={handleBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to login
            </Button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  // Main login form
  return (
    <Card className="border-border bg-card/80 backdrop-blur animate-fade-in-up">
      <CardHeader className="space-y-1 text-center">
        <div className="flex justify-center mb-4 lg:hidden">
          <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
            <Shield className="h-6 w-6 text-red-400" />
          </div>
        </div>
        <CardTitle className="text-2xl">Admin Sign In</CardTitle>
        <CardDescription>
          Sign in with your admin credentials
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {resetSuccess && (
            <div className="p-3 text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              Password reset successful! Please sign in with your new password.
            </div>
          )}
          {error && (
            <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="bg-muted/50 border-border focus:border-red-500/50 pl-10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <button
                type="button"
                onClick={handleStartForgotPassword}
                className="text-xs text-red-400 hover:text-red-300 hover:underline font-medium"
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="bg-muted/50 border-border focus:border-red-500/50 pl-10"
              />
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button
            type="submit"
            className="w-full bg-red-500 hover:bg-red-400 text-white font-semibold"
            disabled={isLoading}
          >
            {isLoading ? "Signing in..." : "Sign in"}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
